import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { useCompanyProject } from '../providers/CompanyProjectProvider';
import * as companiesApi from '../services/companiesApi';

export const HomePage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { currentCompany, companies, loading: companyLoading, loadCompanies } = useCompanyProject();
  const navigate = useNavigate();
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    // Redirect authenticated users to omnichannel page
    if (!isLoading && isAuthenticated && !companyLoading) {
      if (companies.length > 0) {
        // If user has companies, redirect to omnichannel (main page)
        navigate('/omnichannel', { replace: true });
      }
      // If user has no companies, show the create company form (handled in render)
    }
  }, [isAuthenticated, isLoading, companyLoading, companies.length, navigate]);

  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCompanyName.trim()) return;

    try {
      setCreating(true);
      const newCompany = await companiesApi.createCompany({ name: newCompanyName.trim() });
      await loadCompanies();
      // After creating, navigate to the new company page
      navigate(`/companies/${newCompany.id}`, { replace: true });
    } catch (err: any) {
      console.error('Error creating company:', err);
      alert(err.response?.data?.detail || 'Failed to create company');
    } finally {
      setCreating(false);
      setNewCompanyName('');
      setShowCreateCompany(false);
    }
  };

  // Show loading state while checking auth
  if (isLoading || (isAuthenticated && companyLoading)) {
    return (
      <div className="container py-12">
        <div className="text-center">
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If authenticated and has companies, don't show the landing page (will redirect)
  if (isAuthenticated && companies.length > 0) {
    return null;
  }

  // If authenticated but no companies, show create company form
  if (isAuthenticated && companies.length === 0) {
    return (
      <div className="container py-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Welcome to Service Bot
            </h1>
            <p className="text-xl text-gray-600">
              Create your first company to get started
            </p>
          </div>
          
          <div className="card p-8">
            {!showCreateCompany ? (
              <div className="text-center">
                <p className="text-gray-600 mb-6">
                  You don't have any companies yet. Create one to start managing your projects.
                </p>
                <button
                  onClick={() => setShowCreateCompany(true)}
                  className="btn btn-primary"
                >
                  Create Company
                </button>
              </div>
            ) : (
              <form onSubmit={handleCreateCompany}>
                <div className="mb-4">
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    id="companyName"
                    type="text"
                    value={newCompanyName}
                    onChange={(e) => setNewCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    required
                    className="form-input w-full"
                    disabled={creating}
                  />
                </div>
                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={creating || !newCompanyName.trim()}
                  >
                    {creating ? 'Creating...' : 'Create Company'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateCompany(false);
                      setNewCompanyName('');
                    }}
                    className="btn btn-outline"
                    disabled={creating}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-12">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">
          Welcome to Service Bot
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          AI-powered automation platform built with FastAPI and React. 
          Create intelligent workflows and automate your business processes.
        </p>
        <div className="flex gap-4 justify-center">
          <a href="/register" className="btn btn-primary">
            Get Started
          </a>
          <a href="/login" className="btn btn-outline">
            Sign In
          </a>
        </div>
      </div>
      
      <div className="mt-16 grid md:grid-cols-3 gap-8">
        <div className="card p-6 text-center">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">Fast & Reliable</h3>
          <p className="text-gray-600">Built with FastAPI for high-performance async operations.</p>
        </div>
        
        <div className="card p-6 text-center">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">AI Powered</h3>
          <p className="text-gray-600">Integrated with OpenAI for intelligent automation.</p>
        </div>
        
        <div className="card p-6 text-center">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">Secure</h3>
          <p className="text-gray-600">JWT authentication and comprehensive security measures.</p>
        </div>
      </div>
    </div>
  );
}; 